import type { OnsiteProduct, OnsitePrice, TrialDiscount, ChargedTotals } from '@/types';
import type { StripeCheckoutAmount, StripeCheckoutSession } from '@stripe/stripe-js';
import { t, useT } from '@/i18n/t';
import { intervalNoun } from '@/i18n/interval';
import { formatMinorUnits } from '@/money';
import type { UpgradeQuote } from '@/api/client';

interface Props {
  // The catalog product/price to preview. Absent when the config fetch hasn't
  // landed (or failed): totals then defer to the live Checkout Session at the
  // payment step.
  product?: OnsiteProduct;
  price?: OnsitePrice;
  // When present (during payment, and on the success screen via the final
  // session) totals are driven by the live Checkout Session, so tax,
  // discounts, and Adaptive Pricing localized amounts render exactly as Stripe
  // computes them — Stripe pre-formats each amount string in the session
  // currency, so this stays currency-agnostic. Absent before checkout starts,
  // where we preview the catalog price.
  summary?: StripeCheckoutSession | null;
  // The visitor's chosen amount for a variable donation, whose catalog
  // amountCents is 0. Used only for the catalog preview (no live session).
  amountCentsOverride?: number;
  // Set on the trial steps: the free-trial length in days. Renders a $0.00
  // due-today line and a "then {price} after your N-day free trial" note. A
  // trial has no live Checkout Session, so this only affects the catalog preview.
  trialPeriodDays?: number;
  // A resolved promo (trial form, trial-denial acknowledgment, campaign code).
  // Without `charged` it adds a discount note to the catalog preview and the
  // total stays the catalog price; with `charged` the total and discount line
  // come from those first-charge totals and the note states later charges.
  discount?: TrialDiscount | null;
  // First-charge totals: actuals from the completed convert's invoice (success
  // screen), Stripe's invoice preview on the trial-denial acknowledgment, or a
  // campaign code's server-computed discounted amount at the email step. The
  // summary shows the discount as a line and, with `discount`, notes what
  // later charges cost instead of the future-tense discount note.
  charged?: ChargedTotals | null;
}

export function formatAmount(amountCents: number, currency: string): string {
  return formatMinorUnits(amountCents, currency);
}

// One-time and donation prices ship billingInterval "none" as an inert sentinel;
// only real cadences (month/year/…) belong in the amount suffix.
export function cadenceSuffix(interval?: string, count = 1): string {
  if (!interval || interval === 'none') {
    return '';
  }
  if (count > 1) {
    return ` ${t('paywall.intervalEvery', { n: count, count, interval: intervalNoun(interval, count) })}`;
  }
  return t('orderSummary.cadence', { interval: intervalNoun(interval, 1) });
}

function formatCatalogPrice(price: OnsitePrice, amountCentsOverride?: number): string {
  const formatted = formatAmount(amountCentsOverride ?? price.amountCents, price.currency);
  return `${formatted}${cadenceSuffix(price.billingInterval, price.billingIntervalCount)}`;
}

// The fallback when no catalog product is available (config not yet loaded or
// failed): a neutral label until the live session loads.
function summaryProductName(product?: OnsiteProduct): string {
  return product?.name ?? t('orderSummary.defaultProductName');
}

// The purchase kind comes from the price, not the product: legacy
// subscription/one_time product types are canonicalized to "access"
// server-side, and an access product can hold both recurring and one-time
// prices, so product.type can't say which a given checkout is. `recurring` is
// undefined when neither a selected price nor a live session can say
// (direct-checkout preview), which gets the neutral label.
function purchaseKindLabel(product: OnsiteProduct | undefined, recurring: boolean | undefined): string {
  if (product?.type === 'donation') {
    return t('orderSummary.kind.support');
  }
  if (recurring === true) {
    return t('orderSummary.kind.subscription');
  }
  if (recurring === false) {
    return t('orderSummary.kind.oneTime');
  }
  return t('orderSummary.kind.purchase');
}

function hasAmount(amount: StripeCheckoutAmount): boolean {
  return amount.minorUnitsAmount > 0;
}

// Every summary root carries the notranslate class: its amounts update live
// (Stripe session) and its tax/discount lines render conditionally, and Google
// Translate swapping those text nodes for <font> wrappers desyncs Preact's diff
// (stale or crashing updates). Only amounts/labels live here, so nothing worth
// translating is lost.
export function OrderSummary({ product, price, summary, amountCentsOverride, trialPeriodDays, discount, charged }: Props) {
  useT();
  if (summary) {
    return <SessionSummary product={product} price={price} summary={summary} />;
  }

  // A trial charges nothing today: show $0.00 due now and the recurring price
  // that begins after the free days. Only the catalog preview renders trials —
  // a trial has no live Checkout Session.
  if (price && trialPeriodDays && trialPeriodDays > 0) {
    return <TrialSummary product={product} price={price} trialPeriodDays={trialPeriodDays} discount={discount} />;
  }

  // No catalog price to preview (config not yet loaded or failed); show the
  // product line and defer the amount to the live session at the payment step.
  if (!price) {
    return (
      <div class="sm-order-summary notranslate" aria-label={t('orderSummary.ariaLabel')}>
        <div class="sm-summary-heading">{t('orderSummary.heading')}</div>
        <div class="sm-summary-product">
          <div class="sm-summary-product-name">{summaryProductName(product)}</div>
          <div class="sm-summary-product-type">{purchaseKindLabel(product, undefined)}</div>
        </div>

        <dl class="sm-summary-lines">
          <div class="sm-summary-line sm-summary-total">
            <dt>{t('orderSummary.totalDueToday')}</dt>
            <dd>{t('orderSummary.calculatedAtCheckout')}</dd>
          </div>
        </dl>

        <p class="sm-summary-note">{t('orderSummary.secureNote')}</p>
      </div>
    );
  }

  const formattedPrice = formatCatalogPrice(price, amountCentsOverride);
  // Due today is one charge, so it never carries the cadence suffix.
  const discountNote = charged ? chargedThenNote(price, discount, charged) : discountNoteText(price, discount, false);
  const formattedTotal = charged
    ? formatAmount(charged.amountCents, charged.currency)
    : formatAmount(amountCentsOverride ?? price.amountCents, price.currency);

  return (
    <div class="sm-order-summary notranslate" aria-label={t('orderSummary.ariaLabel')}>
      <div class="sm-summary-heading">{t('orderSummary.heading')}</div>
      <div class="sm-summary-product">
        <div class="sm-summary-product-name">{summaryProductName(product)}</div>
        <div class="sm-summary-product-type">{purchaseKindLabel(product, price.type === 'recurring')}</div>
      </div>

      <dl class="sm-summary-lines">
        <div class="sm-summary-line">
          <dt>{t('orderSummary.subtotal')}</dt>
          <dd>{formattedPrice}</dd>
        </div>
        {charged != null && charged.discountCents != null && charged.discountCents > 0 && (
          <div class="sm-summary-line sm-summary-discount">
            <dt>{t('orderSummary.discount')}</dt>
            <dd>{'−'}{formatAmount(charged.discountCents, charged.currency)}</dd>
          </div>
        )}
        <div class="sm-summary-line sm-summary-total">
          <dt>{t('orderSummary.totalDueToday')}</dt>
          <dd>{formattedTotal}</dd>
        </div>
      </dl>

      {discountNote && <p class="sm-summary-note sm-summary-discount-note">{discountNote}</p>}
      <p class="sm-summary-note">{t('orderSummary.secureNote')}</p>
    </div>
  );
}

interface TrialSummaryProps {
  product?: OnsiteProduct;
  price: OnsitePrice;
  trialPeriodDays: number;
  discount?: TrialDiscount | null;
}

// discountMagnitude formats the coupon's discount straight from the coupon — no
// client-side price math — so the trial preview can never diverge from what
// Stripe actually charges (rounding, tax, localized/Adaptive Pricing). Returns
// "25%" for a percentage or the amount for a fixed discount. Null for a fixed
// discount in a different currency than the price, which Stripe wouldn't apply.
// Exported so the trial chip label uses the same magnitude as the summary note.
export function discountMagnitude(price: OnsitePrice, discount: TrialDiscount): string | null {
  if (discount.percentOff != null) {
    return `${discount.percentOff}%`;
  }
  if (discount.amountOff != null) {
    if (discount.currency && discount.currency.toLowerCase() !== price.currency.toLowerCase()) {
      return null;
    }
    return formatAmount(discount.amountOff, discount.currency || price.currency);
  }
  return null;
}

// DiscountSpan is which of a recurring price's charges a coupon discounts:
// first = only the first, every = all, {months} = the first N monthly charges,
// unknown = a window the price's cadence can't express exactly.
export type DiscountSpan = 'first' | 'every' | 'unknown' | { months: number };

// The billing cadence a span is measured against: a catalog price, or a live
// session's recurring block mapped onto the same fields.
export interface BillingCadence {
  billingInterval?: string;
  billingIntervalCount?: number;
}

// discountSpan maps a coupon's duration onto a recurring cadence's charges —
// the one source for the promo chip's scope and the "then" notes. Stripe
// discounts every invoice created within durationInMonths of the start,
// exclusive of the end (sandbox: 1 month on monthly and 12 months on yearly
// discount only the first invoice; 13 months on yearly also discounts the
// second).
export function discountSpan(cadence: BillingCadence, discount: Pick<TrialDiscount, 'duration' | 'durationInMonths'>): DiscountSpan {
  if (discount.duration === 'forever') {
    return 'every';
  }
  if (discount.duration === 'once') {
    return 'first';
  }
  const months = discount.durationInMonths;
  if (!months || (cadence.billingIntervalCount ?? 1) !== 1) {
    return 'unknown';
  }
  if (cadence.billingInterval === 'month') {
    return months === 1 ? 'first' : { months };
  }
  if (cadence.billingInterval === 'year' && months <= 12) {
    return 'first';
  }
  return 'unknown';
}

// chargedThenNote states what later charges cost once the first-charge totals
// are known: the catalog price after a first-charge-only discount, or the
// discounted amount for the first N months then the catalog price. Null for a
// forever discount (the total is the recurring price) and for a window the
// cadence can't express — the discount line and chip already state the deal.
function chargedThenNote(price: OnsitePrice, discount: TrialDiscount | null | undefined, charged: ChargedTotals): string | null {
  if (!discount || price.type !== 'recurring' || discountMagnitude(price, discount) == null) {
    return null;
  }
  const span = discountSpan(price, discount);
  if (span === 'first') {
    return t('orderSummary.thenPrice', { price: formatCatalogPrice(price) });
  }
  if (typeof span !== 'object') {
    return null;
  }
  const today = `${formatAmount(charged.amountCents, charged.currency)}${cadenceSuffix(price.billingInterval, price.billingIntervalCount)}`;
  return t('orderSummary.discountedForMonths', { today, n: span.months, count: span.months, price: formatCatalogPrice(price) });
}

// sessionThenNote states what later charges cost, from Stripe's own next-cycle
// totals (recurring.dueNext). dueNext is the next invoice, so it differs from
// today's total only when the discount stops before it; a monthly discount
// that still covers the next invoice gets the month-count form instead, with
// dueNext.subtotal as the post-discount price (exact only without exclusive
// tax). Null for one-time sessions, forever discounts and no discount.
function sessionThenNote(summary: StripeCheckoutSession, suffix: string): string | null {
  const recurring = summary.recurring;
  if (!recurring?.dueNext) {
    return null;
  }
  const { dueNext } = recurring;
  const today = summary.total.total;
  if (dueNext.total.minorUnitsAmount !== today.minorUnitsAmount) {
    return t('orderSummary.thenPrice', { price: `${dueNext.total.amount}${suffix}` });
  }
  if (recurring.interval !== 'month' || recurring.intervalCount !== 1 || hasAmount(dueNext.taxExclusive)) {
    return null;
  }
  const discounts = summary.discountAmounts ?? [];
  const discountWindow = discounts.length === 1 ? discounts[0].recurring : null;
  if (discountWindow?.type !== 'repeating' || discountWindow.durationInMonths <= 1) {
    return null;
  }
  const months = discountWindow.durationInMonths;
  return t('orderSummary.discountedForMonths', { today: `${today.amount}${suffix}`, n: months, count: months, price: `${dueNext.subtotal.amount}${suffix}` });
}

// trialThenNote builds the standard "then …" line under a trial summary. It
// always states the undiscounted recurring price — a discount, when present, is
// called out on its own line by discountNoteText so the base price reads the
// same with or without a code.
function trialThenNote(price: OnsitePrice, days: number): string {
  return t('orderSummary.trialThen', { n: days, price: formatCatalogPrice(price) });
}

// discountNoteText states that a discount will be applied, plus how long it
// lasts, without computing a discounted total (that risks diverging from Stripe
// on rounding/tax/localization). The span comes from discountSpan, like the
// chip: every charge, the first charge (once, or a window covering only the
// first invoice), or the exact month count (the vague "limited time" only when
// the count is missing). onTrial adds the "starts when your trial ends" qualifier —
// the schedule-backed trial's discount window opens at the transition. Null
// when there's no usable discount (e.g. a fixed amount in a currency Stripe
// wouldn't apply to this price).
function discountNoteText(price: OnsitePrice, discount: TrialDiscount | null | undefined, onTrial: boolean): string | null {
  if (!discount) {
    return null;
  }
  const magnitude = discountMagnitude(price, discount);
  if (magnitude == null) {
    return null;
  }
  const span = discountSpan(price, discount);
  if (span === 'every') {
    return t(onTrial ? 'orderSummary.discount.foreverOnTrial' : 'orderSummary.discount.forever', { amount: magnitude });
  }
  if (span === 'first') {
    return t(onTrial ? 'orderSummary.discount.onceOnTrial' : 'orderSummary.discount.once', { amount: magnitude });
  }
  const months = discount.durationInMonths;
  if (months) {
    return t(onTrial ? 'orderSummary.discount.repeatingMonthsOnTrial' : 'orderSummary.discount.repeatingMonths', { n: months, count: months, amount: magnitude });
  }
  return t(onTrial ? 'orderSummary.discount.repeatingOnTrial' : 'orderSummary.discount.repeating', { amount: magnitude });
}

function TrialSummary({ product, price, trialPeriodDays, discount }: TrialSummaryProps) {
  useT();
  const dueToday = formatAmount(0, price.currency);
  const dayLabel = t('orderSummary.trialDays', { n: trialPeriodDays });
  const discountNote = discountNoteText(price, discount, true);

  return (
    <div class="sm-order-summary notranslate" aria-label={t('orderSummary.ariaLabel')}>
      <div class="sm-summary-heading">{t('orderSummary.heading')}</div>
      <div class="sm-summary-product">
        <div class="sm-summary-product-name">{summaryProductName(product)}</div>
        <div class="sm-summary-product-type">{purchaseKindLabel(product, price.type === 'recurring')}</div>
      </div>

      <dl class="sm-summary-lines">
        <div class="sm-summary-line">
          <dt>{t('orderSummary.freeTrial')}</dt>
          <dd>{dayLabel}</dd>
        </div>
        <div class="sm-summary-line sm-summary-total">
          <dt>{t('orderSummary.totalDueToday')}</dt>
          <dd>{dueToday}</dd>
        </div>
      </dl>

      <p class="sm-summary-note">{trialThenNote(price, trialPeriodDays)}</p>
      {discountNote && <p class="sm-summary-note sm-summary-discount-note">{discountNote}</p>}
    </div>
  );
}

interface SessionSummaryProps {
  product?: OnsiteProduct;
  price?: OnsitePrice;
  summary: StripeCheckoutSession;
}

function SessionSummary({ product, price, summary }: SessionSummaryProps) {
  useT();
  const total = summary.total;
  const suffix = summary.recurring
    ? cadenceSuffix(summary.recurring.interval, summary.recurring.intervalCount)
    : cadenceSuffix(price?.billingInterval, price?.billingIntervalCount);
  // A per-item purchase passes no price; the live session's recurring block
  // says whether the charge repeats.
  const recurring = price ? price.type === 'recurring' : summary.recurring != null;
  const showDiscount = hasAmount(total.discount);
  // Only exclusive tax adds to the amount due; inclusive tax is already baked
  // into the subtotal/total, so it needs no separate line.
  const showTax = hasAmount(total.taxExclusive);
  const thenNote = sessionThenNote(summary, suffix);

  return (
    <div class="sm-order-summary notranslate" aria-label={t('orderSummary.ariaLabel')}>
      <div class="sm-summary-heading">{t('orderSummary.heading')}</div>
      <div class="sm-summary-product">
        <div class="sm-summary-product-name">{summaryProductName(product)}</div>
        <div class="sm-summary-product-type">{purchaseKindLabel(product, recurring)}</div>
      </div>

      <dl class="sm-summary-lines">
        <div class="sm-summary-line">
          <dt>{t('orderSummary.subtotal')}</dt>
          <dd>{total.subtotal.amount}{suffix}</dd>
        </div>
        {showDiscount && (
          <div class="sm-summary-line sm-summary-discount">
            <dt>{t('orderSummary.discount')}</dt>
            <dd>{'\u2212'}{total.discount.amount}</dd>
          </div>
        )}
        {showTax && (
          <div class="sm-summary-line">
            <dt>{t('orderSummary.tax')}</dt>
            <dd>{total.taxExclusive.amount}</dd>
          </div>
        )}
        <div class="sm-summary-line sm-summary-total">
          <dt>{t('orderSummary.totalDueToday')}</dt>
          <dd>{total.total.amount}</dd>
        </div>
      </dl>

      {thenNote && <p class="sm-summary-note sm-summary-discount-note">{thenNote}</p>}
      <p class="sm-summary-note">{t('orderSummary.secureNote')}</p>
    </div>
  );
}

// UpgradeSummary is the confirm step's order summary for an upgrade: the new
// plan, Stripe's unused-time credit and the amount due from the server quote.
export function UpgradeSummary({ quote, detail, renewsAt }: { quote: UpgradeQuote; detail: string; renewsAt: string }) {
  useT();
  const recurringPrice = `${formatMinorUnits(quote.newAmountCents, quote.currency)}${cadenceSuffix(quote.billingInterval, quote.billingIntervalCount)}`;
  const carryover = quote.carryoverCreditCents ?? 0;
  return (
    <section class="sm-order-summary sm-upgrade-summary notranslate" aria-label={t('orderSummary.ariaLabel')}>
      <div class="sm-summary-heading">{t('orderSummary.heading')}</div>
      <div class="sm-summary-product">
        <div class="sm-summary-product-name">{quote.toProductName}</div>
        {detail && <div class="sm-summary-product-type">{detail}</div>}
      </div>
      <dl class="sm-summary-lines">
        <div class="sm-summary-line">
          <dt>{t('orderSummary.subtotal')}</dt>
          <dd>{recurringPrice}</dd>
        </div>
        {quote.creditCents > 0 && (
          <div class="sm-summary-line sm-summary-discount">
            <dt>{t('upgrade.unusedCredit')}</dt>
            <dd>{'\u2212'}{formatMinorUnits(quote.creditCents, quote.currency)}</dd>
          </div>
        )}
        {quote.adjustmentCents !== 0 && (
          <div class={`sm-summary-line${quote.adjustmentCents < 0 ? ' sm-summary-discount' : ''}`}>
            <dt>{t('upgrade.otherAdjustments')}</dt>
            <dd>{quote.adjustmentCents < 0 ? '\u2212' : ''}{formatMinorUnits(Math.abs(quote.adjustmentCents), quote.currency)}</dd>
          </div>
        )}
        <div class="sm-summary-line sm-summary-total">
          <dt>{t('orderSummary.totalDueToday')}</dt>
          <dd>{formatMinorUnits(quote.amountDueCents, quote.currency)}</dd>
        </div>
      </dl>
      {quote.presentmentCurrency && quote.approxPresentmentAmountDue !== undefined && <p class="sm-summary-note sm-upgrade-local">{t('upgrade.approxLocal', { amount: formatMinorUnits(quote.approxPresentmentAmountDue, quote.presentmentCurrency) })}</p>}
      {carryover > 0 && <p class="sm-summary-note">{t('upgrade.creditCarryover', { amount: formatMinorUnits(carryover, quote.currency) })}</p>}
      {quote.convertsTrial && <p class="sm-summary-note">{t('upgrade.trialEndsNote')}</p>}
      <p class="sm-summary-note">{t('upgrade.thenFrom', { price: recurringPrice, date: renewsAt })}</p>
    </section>
  );
}
